declare global {
  interface Window {
    UEDITOR_HOME_URL?: string;
    UE?: any;
    baidu?: {
      editor?: any;
      [key: string]: any;
    };
  }
}

const scriptPromises = new Map<string, Promise<void>>();
const stylePromises = new Map<string, Promise<void>>();
let ensurePromise: Promise<void> | null = null;
let manualEvaluationAttempted = false;

function resolveBaseUrl(): string {
  const publicUrl = process.env.PUBLIC_URL || "";

  if (!publicUrl || publicUrl === "/") {
    return "/lib/ueditor/";
  }

  if (publicUrl.endsWith("/")) {
    return `${publicUrl}lib/ueditor/`;
  }

  return `${publicUrl}/lib/ueditor/`;
}

function getScriptUrls(baseUrl: string): string[] {
  return [
    `${baseUrl}ueditor.config.js`,
    `${baseUrl}ueditor.all.js`,
    `${baseUrl}lang/zh-cn/zh-cn.js`,
  ];
}

function getStyleUrl(baseUrl: string): string {
  return `${baseUrl}themes/default/css/ueditor.css`;
}

function getUEditorGlobal(): any | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  if (window.UE) {
    return window.UE;
  }
  if (window.baidu && window.baidu.editor) {
    return window.baidu.editor;
  }
  return undefined;
}

function syncUEditorGlobals(): void {
  if (typeof window === "undefined") {
    return;
  }
  const ue = getUEditorGlobal();
  if (!ue) {
    return;
  }
  if (!window.UE) {
    window.UE = ue;
  }
  if (!window.baidu) {
    window.baidu = { editor: ue };
  } else if (window.baidu.editor !== ue) {
    window.baidu.editor = ue;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function waitForUEditorReady(
  timeoutMs = 4000,
  intervalMs = 50,
): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() <= deadline) {
    const ue = getUEditorGlobal();
    if (ue && typeof ue.getEditor === "function") {
      syncUEditorGlobals();
      return;
    }
    await sleep(intervalMs);
  }
  throw new Error("UEditor scripts loaded but window.UE is still unavailable.");
}

function ensureErrorWithContext(message: string): Error {
  if (typeof window === "undefined") {
    return new Error(message);
  }
  const snapshot = {
    hasUE: !!window.UE,
    ueKeys: window.UE ? Object.keys(window.UE) : [],
    hasBaidu: !!window.baidu,
    baiduKeys: window.baidu ? Object.keys(window.baidu) : [],
    hasConfig: typeof (window as any).UEDITOR_CONFIG === "object",
  };
  const error = new Error(`${message} Debug:${JSON.stringify(snapshot)}`);
  return error;
}

function fetchScriptContent(url: string): Promise<string> {
  if (typeof window === "undefined") {
    return Promise.reject(
      new Error("Cannot fetch scripts outside the browser environment."),
    );
  }

  if (typeof window.fetch === "function") {
    return window
      .fetch(url, { credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to fetch script: ${url} (status ${response.status})`,
          );
        }
        return response.text();
      });
  }

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("GET", url, true);
    request.onreadystatechange = () => {
      if (request.readyState !== 4) {
        return;
      }
      if (request.status >= 200 && request.status < 300) {
        resolve(request.responseText);
        return;
      }
      reject(
        new Error(`Failed to fetch script: ${url} (status ${request.status})`),
      );
    };
    request.onerror = () => {
      reject(new Error(`Network error while fetching script: ${url}`));
    };
    request.send();
  });
}

async function evaluateScriptFromUrl(url: string): Promise<void> {
  const code = await fetchScriptContent(url);
  try {
    const wrapped =
      "(function(){\n" +
      code +
      "\n}).call(window);\n//# sourceURL=" +
      url +
      "\n";
    // eslint-disable-next-line no-new-func
    const evaluator = new Function("window", "document", wrapped);
    evaluator.call(window, window, document);
  } catch (error) {
    throw new Error(
      `Failed to evaluate script: ${url}. ${(error as Error).message}`,
    );
  }
}

async function manuallyEvaluateScripts(urls: string[]): Promise<void> {
  for (const url of urls) {
    await evaluateScriptFromUrl(url);
  }
  syncUEditorGlobals();
}

function appendScript(src: string): Promise<void> {
  const cached = scriptPromises.get(src);
  if (cached) {
    return cached;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const existing = Array.from(
      document.querySelectorAll<HTMLScriptElement>(
        'script[data-ueditor-loader="script"]',
      ),
    ).find((script) => script.dataset.src === src);

    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => {
          scriptPromises.delete(src);
          reject(new Error(`Failed to load UEditor script: ${src}`));
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.type = "text/javascript";
    script.async = false;
    script.charset = "utf-8";
    script.dataset.ueditorLoader = "script";
    script.dataset.src = src;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => {
      scriptPromises.delete(src);
      reject(new Error(`Failed to load UEditor script: ${src}`));
    };
    document.head.appendChild(script);
  });

  scriptPromises.set(src, promise);
  return promise;
}

function appendStyle(href: string): Promise<void> {
  const cached = stylePromises.get(href);
  if (cached) {
    return cached;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const existing = Array.from(
      document.querySelectorAll<HTMLLinkElement>(
        'link[data-ueditor-loader="style"]',
      ),
    ).find((link) => link.dataset.href === href);

    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => {
          stylePromises.delete(href);
          reject(new Error(`Failed to load UEditor stylesheet: ${href}`));
        },
        { once: true },
      );
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.type = "text/css";
    link.href = href;
    link.dataset.ueditorLoader = "style";
    link.dataset.href = href;
    link.onload = () => {
      link.dataset.loaded = "true";
      resolve();
    };
    link.onerror = () => {
      stylePromises.delete(href);
      reject(new Error(`Failed to load UEditor stylesheet: ${href}`));
    };
    document.head.appendChild(link);
  });

  stylePromises.set(href, promise);
  return promise;
}

export function ensureUEditor(): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("UEditor can only be loaded in a browser environment."),
    );
  }

  syncUEditorGlobals();
  const existingUE = getUEditorGlobal();
  if (existingUE && typeof existingUE.getEditor === "function") {
    return Promise.resolve();
  }

  if (!ensurePromise) {
    ensurePromise = (async () => {
      const baseUrl = resolveBaseUrl();
      window.UEDITOR_HOME_URL = baseUrl;

      try {
        await appendStyle(getStyleUrl(baseUrl));
        for (const src of getScriptUrls(baseUrl)) {
          await appendScript(src);
        }
      } catch (error) {
        ensurePromise = null;
        throw error;
      }

      const scriptUrls = getScriptUrls(baseUrl);
      try {
        await waitForUEditorReady();
        manualEvaluationAttempted = false;
      } catch (error) {
        if (!manualEvaluationAttempted) {
          manualEvaluationAttempted = true;
          try {
            await manuallyEvaluateScripts(scriptUrls);
            await waitForUEditorReady();
            manualEvaluationAttempted = false;
          } catch (manualError) {
            manualEvaluationAttempted = false;
            ensurePromise = null;
            throw ensureErrorWithContext(
              (manualError as Error).message ||
                "UEditor initialization failed.",
            );
          }
        } else {
          manualEvaluationAttempted = false;
          ensurePromise = null;
          throw ensureErrorWithContext(
            (error as Error).message || "UEditor initialization failed.",
          );
        }
      }
    })();
  }

  return ensurePromise;
}

export function getUEditorBaseUrl(): string {
  return resolveBaseUrl();
}
